import DynamicSectionElement from '@/js/dynamic-section-element';
import { debounce } from '@/js/helpers';

class PredictiveSearch extends DynamicSectionElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = document.querySelector(
      '[data-predictive-search-results]'
    );
    this.searchInputWrapper = document.querySelector(
      '[data-search-input-wrapper]'
    );

    this.registerEvents();
  }

  registerEvents() {
    this.input.addEventListener(
      'input',
      debounce(event => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.mainContent = document.querySelector('#MainContent');
    this.mainContent.addEventListener('click', event => {
      // if the click is on any part of the header (includes the search), do nothing
      if (event.target.closest('.site-header')) return;
      // if the search is open; close it.
      const searchInputWrapper = document.querySelector(
        '[data-search-input-wrapper]'
      );

      if (
        searchInputWrapper &&
        Object.hasOwn(searchInputWrapper.dataset, 'active')
      ) {
        this.closeSearch();
      }
    });
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (searchTerm.length === 0) {
      this.closeSearch();
      return;
    }
    this.getSearchResults(searchTerm);
  }

  /**
   * Uses section rendering to make an ajax request to return search related products
   * https://shopify.dev/api/ajax/reference/predictive-search
   * @param {string} searchTerm
   */
  getSearchResults(searchTerm) {
    /* Note! We are not using shopify's predictive search api! It is not compatible with stores with a default language of Japanese.
     instead we are using section rendering to return the search results page with a custom template. 
     See https://shopify.dev/docs/api/ajax/reference/predictive-search#supported-languages 
     */

    const template = 'preview';

    fetch(
      `${Shopify.routes.root}search/?q=${encodeURIComponent(
        searchTerm
      )}&view=${template}`
    )
      .then(response => {
        if (!response.ok) {
          const error = new Error(response.status);
          // eslint-disable-next-line no-console
          console.error('Search Failed', error);
          this.closeSearch();
        }

        return response.text();
      })
      .then(text => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('[data-predictive-search-results]').innerHTML;
        this.predictiveSearchResults.innerHTML = resultsMarkup;

        this.openSearch();
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Search Failed', error);
        this.closeSearch();
      });
  }

  /**
   * Opens search panel
   * @return {void}
   */
  openSearch() {
    this.predictiveSearchResults.classList.add('is-open');
    document.querySelector('body').setAttribute('predictive-search-open', '');
  }

  /**
   * Clears the search results
   */
  clearResults() {
    this.predictiveSearchResults.innerHTML = '';
    this.predictiveSearchResults.removeEventListener(
      'animationend',
      this.clearResults
    );
  }

  /**
   * Closes the search panel
   * @return {void}
   */
  closeSearch() {
    document.querySelector('body').removeAttribute('predictive-search-open');
    this.predictiveSearchResults.classList.remove('is-open');

    this.predictiveSearchResults.addEventListener(
      'animationend',
      this.clearResults
    );
  }

  /**
   * Close out the search and searched content when the panel is opened
   * @returns {void}
   */
  closePredictiveSearch() {
    // Close out the search and searched content when the panel is opened
    if (
      this.searchInputWrapper &&
      Object.hasOwn(this.searchInputWrapper.dataset, 'active')
    ) {
      delete this.searchInputWrapper.dataset.active;
      document.querySelector('predictive-search').closeSearch();
    }
  }
}

customElements.get('predictive-search') ||
  customElements.define('predictive-search', PredictiveSearch);
